import { PhoneNumberUtil } from 'google-libphonenumber'
import { z } from 'zod'

import mxTranslations from '../i18n/messages/es-MX.json'
import { TStoreConfig } from '../store'

// 定义 地址表单 校验规则
export const addressFormSchema = (phoneUtil?: PhoneNumberUtil, storeConfig?: TStoreConfig) =>
  z
    .object({
      country_code: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is required field.' }),
      firstname: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is a required field.' })
        .max(255, {
          message:
            mxTranslations?.Common?.input_max_255 || 'Please enter less or equal than 255 symbols.',
        })
        .regex(/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' -]+$/, {
          message:
            mxTranslations?.Common?.letters_only ||
            'Only letters, spaces, apostrophes and hyphens are allowed',
        }),

      lastname: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is a required field.' })
        .max(255, {
          message:
            mxTranslations?.Common?.input_max_255 || 'Please enter less or equal than 255 symbols.',
        })
        .regex(/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' -]+$/, {
          message:
            mxTranslations?.Common?.letters_only ||
            'Only letters, spaces, apostrophes and hyphens are allowed',
        }),
      company: z
        .string()
        .trim()
        .max(255, mxTranslations?.Common?.company_too_long || 'Company is too long')
        .optional(),
      street: z.tuple([
        // street[0] 第一个地址是必填
        z
          .string()
          .trim()
          .min(1, { message: mxTranslations?.Common?.required_field || 'This is required' })
          .max(255, mxTranslations?.Common?.street_too_long || 'Street is too long'),
        // street[1] 第二个地址是非必填
        z
          .string()
          .max(255, mxTranslations?.Common?.street_too_long || 'Street is too long')
          .optional()
          .default(''),
        // street[2] 第三个地址是非必填
        z
          .string()
          .max(255, mxTranslations?.Common?.street_too_long || 'Street is too long')
          .optional()
          .default(''),
      ]),
      city: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is required field.' })
        .max(255, mxTranslations?.Common?.city_too_long || 'City is too long')
        .regex(
          /^[A-Za-z\s]+$/,
          mxTranslations?.Common?.letters_spaces_only || 'Only letters and spaces allowed',
        ),
      region: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is required field.' }),
      region_id: z
        .string()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is require field.' }),
      postcode: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is required field.' }),
      // .regex(/^\d{5}(-\d{4})?$/, 'Invalid postcode format'),
      telephone: z
        .string()
        .trim()
        .min(1, { message: mxTranslations?.Common?.required_field || 'This is required field.' })
        .max(20, mxTranslations?.Common?.phone_too_long || 'Phone number is too long'),
      /*.regex(
      /^(\(\d{3}\)|\d{3})[- ]?\d{3}[- ]?\d{4}$/,
      'Please enter a valid 10-digit phone numbers e.g.555-555-1234.',
    ), // 1234567890 / 123-456-7890 / 123 456 7890 / (123) 456-7890*/
      default: z.boolean().optional(),
      save_in_address_book: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const { postcode, telephone, country_code } = data
      const zipCodeRules = storeConfig?.zip_code_regular || [
        {
          country_code: 'CA',
          regular: [
            {
              example: 'A1B 2C3',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}\\s[0-9]{1}[a-zA-z]{1}[0-9]{1}$',
            },
            {
              example: 'A1B2C3',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}[0-9]{1}$',
            },
            {
              example: 'A1B',
              pattern: '^[a-zA-z]{1}[0-9]{1}[a-zA-z]{1}$',
            },
          ],
        },
        {
          country_code: 'MX',
          regular: [
            {
              example: '12345',
              pattern: '^[0-9]{5}$',
            },
          ],
        },
        {
          country_code: 'US',
          regular: [
            {
              example: '12345-6789',
              pattern: '^[0-9]{5}\\-[0-9]{4}$',
            },
            {
              example: '12345',
              pattern: '^[0-9]{5}$',
            },
          ],
        },
      ]

      // 🔍 postcode
      const rule = zipCodeRules.find((r) => r?.country_code === country_code)
      if (rule) {
        const match = rule?.regular?.some((r) => new RegExp(r?.pattern || '').test(postcode.trim()))
        if (!match) {
          const examples = rule?.regular?.map((r) => r?.example).join(', ')

          ctx.addIssue({
            path: ['postcode'],
            code: z.ZodIssueCode.custom,
            message: `${mxTranslations?.Common?.invalid_postcode_for || 'Invalid postcode format for'} ${country_code}. ${mxTranslations?.Common?.valid_examples || 'Valid examples are:'} ${examples}`,
          })
        }
      }

      // 🔍 telephone
      try {
        const parsed = phoneUtil?.parseAndKeepRawInput(telephone, country_code)
        if (!!parsed && !phoneUtil?.isValidNumberForRegion(parsed, country_code)) {
          ctx.addIssue({
            path: ['telephone'],
            code: z.ZodIssueCode.custom,
            message: `${mxTranslations?.Common?.invalid_phone_for || 'Invalid phone number for'} ${country_code}`,
          })
        }
      } catch {
        ctx.addIssue({
          path: ['telephone'],
          code: z.ZodIssueCode.custom,
          message: mxTranslations?.Common?.invalid_phone_format || 'Invalid phone number format',
        })
      }
    })

// 导出 地址表单 类型
export type TAddressFormValues = z.infer<ReturnType<typeof addressFormSchema>>
